<nb-card #wizardCard>
  <nb-card-header class="wizard-header">
    <h2>{{ 'tutor.wizard.title' | translate }}</h2>
    <button
      nbButton
      ghost
      size="small"
      class="wizard-header__close"
      [attr.aria-label]="'buttons.close' | translate"
      (click)="cancel()"
    >
      <nb-icon icon="close-outline"></nb-icon>
    </button>
  </nb-card-header>

  <nb-card-body>
    <!-- Phase indicator: 3 logical phases group the stepper steps so
         admins know where they are in the bigger flow (Allgemein →
         Konfiguration → Zuweisung & Review). The grid shares the same
         horizontal scale as the stepper so each phase visually spans
         exactly the steps it contains. -->
    <div
      class="wizard-phases"
      [style.grid-template-columns]="phaseGridTemplate"
    >
      <div
        *ngFor="let key of PHASE_LABEL_KEYS; let i = index"
        class="wizard-phase"
        [class.wizard-phase--active]="currentPhaseIndex === i"
        [class.wizard-phase--done]="currentPhaseIndex > i"
      >
        <span class="wizard-phase__label">{{ key | translate }}</span>
      </div>
    </div>

    <!-- Stepper waits for the first settings-load. Nebular captures
         the `<nb-step>` QueryList at init; if we start with
         `visibleSections=[]` and later push 8 more steps via *ngFor
         the stepper indicator silently sticks on the original 3
         steps and the wizard appears to hang. With this guard the
         stepper only mounts once the section list is final. -->
    <app-page-loader *ngIf="loading || visibleSections.length === 0"></app-page-loader>
    <nb-stepper
      *ngIf="!loading && visibleSections.length > 0"
      #stepper
      orientation="horizontal"
      [linear]="false"
      (stepChange)="onStepChange($event)"
    >

      <!-- Step 1 — "Allgemein": purely admin-facing meta about the tutor
           (name, internal description, template choice). Kept separate
           from the AI-config sections below so the first step has a
           single, focused job: register the tutor itself. -->
      <nb-step [label]="'tutor.wizard.steps.general' | translate">
        <p class="step-guidance">{{ 'tutor.wizard.stepGuidance.general' | translate }}</p>

        <div class="wizard-step">
          <div [formGroup]="metaForm" class="wizard-tiles">
            <div class="wizard-tile">
              <h5 class="wizard-tile__title">{{ 'tutor.wizard.tiles.basics' | translate }}</h5>

              <div class="field">
                <label class="field__label">{{ 'tutor.wizard.name' | translate }}</label>
                <input
                  nbInput
                  fullWidth
                  formControlName="name"
                  [status]="showMetaError('name') ? 'danger' : 'basic'"
                />
                <details class="field__example-toggle" *ngIf="metaExample('name')">
                  <summary>{{ EXAMPLE_TOGGLE_KEY | translate }}</summary>
                  <p class="field__example" [innerHTML]="metaExample('name')"></p>
                </details>
              </div>

              <div class="field">
                <label class="field__label">{{ 'tutor.wizard.description' | translate }}</label>
                <textarea nbInput fullWidth formControlName="description" rows="2"></textarea>
                <details class="field__example-toggle" *ngIf="metaExample('description')">
                  <summary>{{ EXAMPLE_TOGGLE_KEY | translate }}</summary>
                  <p class="field__example" [innerHTML]="metaExample('description')"></p>
                </details>
              </div>

              <div class="field">
                <label class="field__label">{{ 'tutor.wizard.template' | translate }}</label>
                <ng-container *ngIf="existingTutor; else templateSelect">
                  <input
                    nbInput
                    fullWidth
                    readonly
                    [value]="
                      activeTemplateLabel
                        ? ('enums.assistantTemplate.' + activeTemplateLabel | translate)
                        : ''
                    "
                  />
                </ng-container>
                <ng-template #templateSelect>
                  <nb-select
                    fullWidth
                    formControlName="template_id"
                    (selectedChange)="onTemplateChange($event)"
                  >
                    <nb-option *ngFor="let t of creatableTemplates" [value]="t.id">
                      {{ "enums.assistantTemplate." + t.name | translate }}
                    </nb-option>
                  </nb-select>
                </ng-template>
              </div>
            </div>

            <!-- Learner-facing tile: what the learner sees in the chat
                 widget (display name, welcome message, chat styling). -->
            <div class="wizard-tile">
              <h5 class="wizard-tile__title">{{ 'tutor.wizard.tiles.learnerFacing' | translate }}</h5>

              <div class="field">
                <label class="field__label">{{ 'tutor.wizard.displayName' | translate }}</label>
                <input
                  nbInput
                  fullWidth
                  formControlName="visual_name"
                  [placeholder]="'tutor.wizard.displayNamePlaceholder' | translate"
                />
                <details class="field__example-toggle" *ngIf="metaExample('displayName')">
                  <summary>{{ EXAMPLE_TOGGLE_KEY | translate }}</summary>
                  <p class="field__example" [innerHTML]="metaExample('displayName')"></p>
                </details>
              </div>

              <div class="field">
                <label class="field__label">{{ 'tutor.wizard.welcomeMessage' | translate }}</label>
                <textarea
                  nbInput
                  fullWidth
                  formControlName="welcome_message"
                  rows="2"
                  [placeholder]="'tutor.wizard.welcomeMessagePlaceholder' | translate"
                ></textarea>
                <details class="field__example-toggle" *ngIf="metaExample('welcomeMessage')">
                  <summary>{{ EXAMPLE_TOGGLE_KEY | translate }}</summary>
                  <p class="field__example" [innerHTML]="metaExample('welcomeMessage')"></p>
                </details>
              </div>

              <div class="field">
                <label class="field__label">{{ 'tutor.wizard.chatStyle' | translate }}</label>
                <nb-select
                  fullWidth
                  formControlName="widget_setting_id"
                  [placeholder]="'tutor.wizard.chatStylePlaceholder' | translate"
                >
                  <nb-option [value]="''">
                    {{ 'tutor.wizard.chatStyleDefault' | translate }}
                  </nb-option>
                  <nb-option
                    *ngFor="let s of chatService.chatSettings$ | async"
                    [value]="s.id"
                  >{{ s.widget_instance }}</nb-option>
                </nb-select>
                <details class="field__example-toggle" *ngIf="metaExample('chatStyle')">
                  <summary>{{ EXAMPLE_TOGGLE_KEY | translate }}</summary>
                  <p class="field__example" [innerHTML]="metaExample('chatStyle')"></p>
                </details>
              </div>
            </div>
          </div>
        </div>

        <div class="step-footer step-footer--between">
          <button nbButton status="basic" (click)="cancel()">
            {{ 'buttons.cancel' | translate }}
          </button>
          <button nbButton status="primary" nbStepperNext>
            {{ 'buttons.next' | translate }}
          </button>
        </div>
      </nb-step>

      <!-- AI-configuration steps — driven by AI_SETTINGS_SECTIONS so the
           wizard grouping matches the regular AI configuration page. -->
      <nb-step
        *ngFor="let section of visibleSections; let sectionIdx = index"
        [label]="section.labelKey | translate"
      >
        <p class="step-guidance">{{ section.descKey | translate }}</p>

        <div class="wizard-step">
          <!-- Tiles inside the section — render tiles that have at
               least one applicable regular OR virtual setting. -->
          <div class="wizard-tiles">
            <ng-container *ngFor="let tile of section.tiles">
              <ng-container *ngIf="getTileSettings(tile).length + getTileVirtualSettings(tile).length > 0">
                <div class="wizard-tile">
                  <h5 class="wizard-tile__title">{{ tile.titleKey | translate }}</h5>
                  <p
                    *ngIf="tile.hintKey"
                    class="wizard-tile__hint"
                  >{{ tile.hintKey | translate }}</p>

                  <!-- Regular template_settings — bind to settingsForm.
                       Settings are grouped by enable-toggle so a toggle
                       and the input it gates share one .field box. -->
                  <div [formGroup]="settingsForm">
                    <ng-container *ngFor="let g of getTileGroups(tile)">
                      <div class="field field--group">
                        <ng-container
                          *ngTemplateOutlet="settingField; context: { setting: g.leader, bare: true }"
                        ></ng-container>
                        <ng-container *ngFor="let d of g.deps">
                          <ng-container *ngIf="isVisible(d)">
                            <ng-container
                              *ngTemplateOutlet="settingField; context: { setting: d, bare: true, sub: true }"
                            ></ng-container>
                          </ng-container>
                        </ng-container>
                      </div>
                    </ng-container>
                  </div>

                  <!-- Virtual settings (bot-column backed, e.g. ai_hint).
                       Bound to metaForm because their value lives on the
                       bots table, not in template_settings. -->
                  <div [formGroup]="metaForm" *ngIf="getTileVirtualSettings(tile).length > 0">
                    <div class="field field--group" *ngFor="let v of getTileVirtualSettings(tile)">
                      <div class="field__toggle-row">
                        <label class="field__inline-label">
                          {{ "enums.aiConfiguration." + v.name | translate }}
                        </label>
                        <nb-toggle [formControlName]="v.bot_field"></nb-toggle>
                      </div>
                      <details
                        class="field__example-toggle"
                        *ngIf="virtualExampleMarkup(v)"
                      >
                        <summary>{{ EXAMPLE_TOGGLE_KEY | translate }}</summary>
                        <p class="field__example" [innerHTML]="virtualExampleMarkup(v)"></p>
                      </details>
                    </div>
                  </div>
                </div>
              </ng-container>
            </ng-container>
          </div>

          <!-- Dozent section: dozent-link picker. Only renders in edit
               mode (existingTutor set) — the picker writes the link to
               a separate table via API and needs a persisted bot id.
               The (linkChanged) callback keeps the wizard's snapshot in
               sync so the kb-matrix's Dozent column shows immediately
               when a dozent is attached, and disappears on detach. -->
          <div
            *ngIf="section.key === 'dozent' && existingTutor"
            class="wizard-tile"
          >
            <h5 class="wizard-tile__title">{{ 'tutor.dozentLink.title' | translate }}</h5>
            <app-dozent-link-picker
              [tutorId]="existingTutor.id"
              (linkChanged)="onDozentLinkChanged($event)"
            ></app-dozent-link-picker>
          </div>

          <!-- Knowledge section: course/KB matrix sub-form when kb_filter_by_course is on -->
          <div
            *ngIf="section.key === 'knowledge' && settingsForm.get('kb_filter_by_course')?.value"
            class="wizard-tile"
          >
            <h5 class="wizard-tile__title">{{ 'tutor.wizard.courseKbMatrix' | translate }}</h5>
            <div class="kb-matrix">
              <div
                class="kb-matrix__row"
                *ngFor="let row of courseKbRows.controls; let i = index"
                [formGroup]="$any(row)"
              >
                <nb-select formControlName="course_id" placeholder="Kurs">
                  <nb-option
                    *ngFor="let c of courseService.courses$ | async"
                    [value]="c.id"
                  >{{ c.title }}</nb-option>
                </nb-select>
                <nb-select
                  formControlName="kb_id"
                  [placeholder]="'tutor.wizard.kbTutorPlaceholder' | translate"
                >
                  <nb-option [value]="''">—</nb-option>
                  <nb-option
                    *ngFor="let d of dataSourcesService.dataSources$ | async"
                    [value]="d.id"
                  >{{ d.name }}</nb-option>
                </nb-select>
                <!-- Dozent KB column: only meaningful when lecturer mode
                     is on for this tutor; otherwise the column would
                     just confuse admins with an unused dropdown. -->
                <!-- Dozent KB column: surfaces when this tutor has an
                     alpha_dozent linked via bot_dozent_link. lecturer_mode
                     was removed in migration 021; attachment is now the
                     source of truth. -->
                <nb-select
                  *ngIf="hasAttachedDozent"
                  formControlName="kb_dozent_id"
                  [placeholder]="'tutor.wizard.kbDozentPlaceholder' | translate"
                >
                  <nb-option [value]="''">—</nb-option>
                  <nb-option
                    *ngFor="let d of dataSourcesService.dataSources$ | async"
                    [value]="d.id"
                  >{{ d.name }}</nb-option>
                </nb-select>
                <input
                  nbInput
                  fullWidth
                  formControlName="moodle_placement_id"
                  placeholder="Moodle-Placement (optional)"
                />
                <nb-checkbox formControlName="is_active">aktiv</nb-checkbox>
                <button nbButton status="basic" size="small" (click)="removeCourseKbRow(i)">–</button>
              </div>
            </div>
            <button nbButton status="basic" size="small" (click)="addCourseKbRow()">
              + {{ 'tutor.wizard.addCourseKbRow' | translate }}
            </button>
          </div>
        </div>

        <div class="step-footer step-footer--between">
          <button
            *ngIf="sectionIdx === 0"
            nbButton
            status="basic"
            (click)="cancel()"
          >{{ 'buttons.cancel' | translate }}</button>
          <button
            *ngIf="sectionIdx > 0"
            nbButton
            status="basic"
            nbStepperPrevious
          >{{ 'buttons.back' | translate }}</button>
          <button nbButton status="primary" nbStepperNext>
            {{ 'buttons.next' | translate }}
          </button>
        </div>
      </nb-step>

      <!-- Knowledge-base assignment step. In edit mode we mount the
           reusable knowledge-select-form so admins can assign buckets
           inline with the wizard flow. In create mode we show a hint
           because no tutor ID exists yet — the assignment becomes
           available immediately after the wizard saves. -->
      <nb-step [label]="'tutor.wizard.steps.knowledgeAssign' | translate">
        <p class="step-guidance">{{ 'tutor.wizard.stepGuidance.knowledgeAssign' | translate }}</p>
        <div class="wizard-step">
          <ng-container *ngIf="existingTutor; else knowledgeAssignPending">
            <!-- Two assignment panes: KBs for the Tutor agent and KBs
                 for the Dozent agent. Same UI component, differentiated
                 by [purpose]. Backend (purpose='tutor'/'dozent') keeps
                 them in separate rows of bot_data_sources. -->
            <div class="wizard-tile">
              <h5 class="wizard-tile__title">{{ 'tutor.wizard.tiles.kbTutor' | translate }}</h5>
              <p class="wizard-tile__hint">{{ 'tutor.wizard.kbTutorHint' | translate }}</p>
              <app-knowledge-select-form
                [tutor]="existingTutor"
                purpose="tutor"
              ></app-knowledge-select-form>
            </div>

            <div class="wizard-tile">
              <h5 class="wizard-tile__title">{{ 'tutor.wizard.tiles.kbDozent' | translate }}</h5>
              <p class="wizard-tile__hint">{{ 'tutor.wizard.kbDozentHint' | translate }}</p>
              <app-knowledge-select-form
                [tutor]="existingTutor"
                purpose="dozent"
              ></app-knowledge-select-form>
            </div>
          </ng-container>
          <ng-template #knowledgeAssignPending>
            <div class="wizard-info-card">
              {{ 'tutor.wizard.knowledgeAssignPending' | translate }}
            </div>
          </ng-template>
        </div>
        <div class="step-footer step-footer--between">
          <button nbButton status="basic" nbStepperPrevious>{{ 'buttons.back' | translate }}</button>
          <button nbButton status="primary" nbStepperNext>{{ 'buttons.next' | translate }}</button>
        </div>
      </nb-step>

      <!-- Final step: review + save. No prompt preview here — the user
           reviews actual fields, not a synthesized snapshot. -->
      <nb-step [label]="'tutor.wizard.steps.review' | translate">
        <p class="step-guidance">{{ 'tutor.wizard.stepGuidance.review' | translate }}</p>
        <div class="wizard-step">
          <p>{{ 'tutor.wizard.reviewHint' | translate }}</p>
        </div>
        <div class="step-footer step-footer--between">
          <button nbButton status="basic" nbStepperPrevious>{{ 'buttons.back' | translate }}</button>
          <button nbButton status="success" (click)="onSubmit()" [disabled]="submitDisabled">
            {{ 'tutor.wizard.createTutor' | translate }}
          </button>
        </div>
      </nb-step>
    </nb-stepper>
  </nb-card-body>
</nb-card>

<!-- Reusable per-setting field renderer — driven by field_type + visibility.
     `bare`: skip the outer .field box (caller already provides one — used
              when grouping a toggle with its dependent inputs).
     `sub`:  apply the sub-field treatment (lighter label, tighter spacing). -->
<ng-template #settingField let-setting="setting" let-bare="bare" let-sub="sub">
  <ng-container *ngIf="isVisible(setting)">
    <ng-container
      *ngTemplateOutlet="bare ? settingFieldBody : settingFieldBoxed; context: { setting: setting, sub: sub }"
    ></ng-container>
  </ng-container>
</ng-template>

<ng-template #settingFieldBoxed let-setting="setting">
  <div class="field" [formGroup]="settingsForm">
    <ng-container
      *ngTemplateOutlet="settingFieldBody; context: { setting: setting }"
    ></ng-container>
  </div>
</ng-template>

<ng-template #settingFieldBody let-setting="setting" let-sub="sub">
  <div [formGroup]="settingsForm" class="field-body" [class.field-body--sub]="sub">
    <ng-container *ngIf="setting.field_type === 'boolean'; else labelledField">
      <!-- Boolean: label on the left, toggle on the right, on one row. -->
      <div class="field__toggle-row">
        <label class="field__inline-label" [for]="setting.variable_name">
          {{ "enums.aiConfiguration." + setting.name | translate }}
        </label>
        <nb-toggle [formControlName]="setting.variable_name!"></nb-toggle>
      </div>
      <details class="field__example-toggle" *ngIf="hasExample(setting)">
        <summary>{{ EXAMPLE_TOGGLE_KEY | translate }}</summary>
        <p class="field__example" [innerHTML]="exampleMarkup(setting)"></p>
      </details>
    </ng-container>

    <ng-template #labelledField>
      <label
        [class]="sub ? 'field__sub-label' : 'field__label'"
        [for]="setting.variable_name"
      >
        {{ "enums.aiConfiguration." + setting.name | translate }}
      </label>

      <app-language-select
        *ngIf="setting.variable_name === 'default_language'"
        [formControlName]="setting.variable_name!"
      ></app-language-select>
      <ng-container
        *ngIf="setting.variable_name !== 'default_language'"
        [ngSwitch]="setting.field_type"
      >
        <input
          *ngSwitchCase="'text'"
          [id]="setting.variable_name"
          nbInput
          fullWidth
          [formControlName]="setting.variable_name!"
        />

        <textarea
          *ngSwitchCase="'longtext'"
          [id]="setting.variable_name"
          nbInput
          fullWidth
          [formControlName]="setting.variable_name!"
          rows="5"
        ></textarea>

        <textarea
          *ngSwitchCase="'code'"
          [id]="setting.variable_name"
          nbInput
          fullWidth
          [formControlName]="setting.variable_name!"
          rows="8"
        ></textarea>

        <input
          *ngSwitchCase="'int'"
          [id]="setting.variable_name"
          nbInput
          fullWidth
          type="number"
          [formControlName]="setting.variable_name!"
          [min]="setting.validation?.min ?? 0"
          [max]="setting.validation?.max ?? 9999999"
        />

        <input
          *ngSwitchCase="'slider'"
          [id]="setting.variable_name"
          type="range"
          [formControlName]="setting.variable_name!"
          [min]="setting.validation?.min ?? 0"
          [max]="setting.validation?.max ?? 1"
          step="0.1"
        />

        <nb-select
          *ngSwitchCase="'dropdown'"
          fullWidth
          [formControlName]="setting.variable_name!"
          [placeholder]="'enums.aiConfigurationPlaceholders.' + setting.name | translate"
        >
          <nb-option *ngFor="let o of setting.options || []" [value]="o">
            {{ "enums." + setting.name + "." + o | translate }}
          </nb-option>
        </nb-select>

        <nb-select
          *ngSwitchCase="'multi-select'"
          fullWidth
          multiple
          [formControlName]="setting.variable_name!"
        >
          <nb-option *ngFor="let o of setting.options || []" [value]="o">
            {{ "enums." + setting.name + "." + o | translate }}
          </nb-option>
        </nb-select>

        <input
          *ngSwitchDefault
          [id]="setting.variable_name"
          nbInput
          fullWidth
          [formControlName]="setting.variable_name!"
        />
      </ng-container>

      <details class="field__example-toggle" *ngIf="hasExample(setting)">
        <summary>{{ EXAMPLE_TOGGLE_KEY | translate }}</summary>
        <p class="field__example" [innerHTML]="exampleMarkup(setting)"></p>
      </details>
    </ng-template>
  </div>
</ng-template>
